import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef, useRef } from "react";
import {
    Box, Tabs, Tab, TextField, Typography, Alert,
} from "@mui/material";
import UnomiConditionBuilder from "./UnomiConditionBuilder";
import {
    parseUnomiConditionToRows,
    buildUnomiConditionFromRows,
    validateUnomiCondition,
    summarizeCondition,
} from "../utils/unomiConditionUtils";

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

/**
 * Hybrid editor: visual builder + JSON + optional AI rules tab.
 */
const SegmentDefinitionEditor = forwardRef(({
    condition,
    onConditionChange,
    brandName,
    editMode = "manual",
    onEditModeChange,
    rules = "",
    onRulesChange,
    showAiTab = false,
    disabled = false,
}, ref) => {
    const [tab, setTab] = useState(0);
    const [combinator, setCombinator] = useState("and");
    const [rows, setRows] = useState([]);
    const [jsonText, setJsonText] = useState("");
    const [jsonError, setJsonError] = useState("");
    const skipConditionSyncRef = useRef(false);

    const syncFromCondition = useCallback((cond) => {
        const parsed = parseUnomiConditionToRows(cond, { dropUnknown: true });
        setCombinator(parsed.combinator);
        setRows(parsed.rows);
        const built = buildUnomiConditionFromRows(parsed.combinator, parsed.rows);
        setJsonText(built ? JSON.stringify(built, null, 2) : (cond ? JSON.stringify(cond, null, 2) : "{}"));
        setJsonError("");
    }, []);

    const emitCondition = useCallback((cond) => {
        const validation = validateUnomiCondition(cond);
        if (!validation.valid) {
            setJsonError(validation.error);
            return false;
        }
        setJsonError("");
        onConditionChange?.(cond);
        return true;
    }, [onConditionChange]);

    const buildCurrentCondition = useCallback(() => {
        if (tab === 1) {
            try {
                return JSON.parse(jsonText);
            } catch {
                return null;
            }
        }
        return buildUnomiConditionFromRows(combinator, rows);
    }, [tab, jsonText, combinator, rows]);

    useImperativeHandle(ref, () => ({
        getBuiltCondition: buildCurrentCondition,
    }), [buildCurrentCondition]);

    useEffect(() => {
        if (skipConditionSyncRef.current) {
            skipConditionSyncRef.current = false;
            return;
        }
        syncFromCondition(condition);
    }, [condition, syncFromCondition]);

    const handleBuilderChange = ({ combinator: c, rows: r }) => {
        setCombinator(c);
        setRows(r);
        const built = buildUnomiConditionFromRows(c, r);
        setJsonText(built ? JSON.stringify(built, null, 2) : "{}");
        skipConditionSyncRef.current = true;
        if (built) {
            emitCondition(built);
        } else {
            onConditionChange?.(null);
        }
    };

    const handleJsonChange = (text) => {
        setJsonText(text);
        try {
            const parsed = JSON.parse(text);
            const validation = validateUnomiCondition(parsed);
            if (!validation.valid) {
                setJsonError(validation.error);
                return;
            }
            setJsonError("");
            const rowState = parseUnomiConditionToRows(parsed, { dropUnknown: true });
            setCombinator(rowState.combinator);
            setRows(rowState.rows);
            skipConditionSyncRef.current = true;
            onConditionChange?.(parsed);
        } catch (e) {
            setJsonError(`JSON invalide: ${e.message}`);
        }
    };

    const displayCondition = buildUnomiConditionFromRows(combinator, rows);
    const summary = summarizeCondition(displayCondition ?? condition, brandName);

    return (
        <Box>
            {summary && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                    <Typography variant="body2">{summary}</Typography>
                </Alert>
            )}

            <Tabs
                value={tab}
                onChange={(_, v) => {
                    setTab(v);
                    if (onEditModeChange) {
                        if (v === 0 || v === 1) onEditModeChange("manual");
                        else if (v === 2) onEditModeChange("ai");
                    }
                }}
                sx={{ mb: 2, minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none" } }}
            >
                <Tab label="Constructeur" />
                <Tab label="JSON" />
                {showAiTab && <Tab label="Assistant IA" />}
            </Tabs>

            {tab === 0 && (
                <UnomiConditionBuilder
                    combinator={combinator}
                    rows={rows}
                    onChange={handleBuilderChange}
                />
            )}

            {tab === 1 && (
                <Box>
                    <TextField
                        fullWidth
                        multiline
                        minRows={10}
                        maxRows={20}
                        value={jsonText}
                        onChange={(e) => handleJsonChange(e.target.value)}
                        disabled={disabled}
                        placeholder='{ "type": "booleanCondition", ... }'
                        sx={fieldSx}
                        InputProps={{ sx: { fontFamily: "monospace", fontSize: 12 } }}
                    />
                    {jsonError && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                            {jsonError}
                        </Typography>
                    )}
                </Box>
            )}

            {showAiTab && tab === 2 && (
                <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    label="Règles du segment (en langage naturel)"
                    placeholder="Ex: clients de Paris qui ont acheté le produit X dans les 30 derniers jours..."
                    value={rules}
                    onChange={(e) => onRulesChange?.(e.target.value)}
                    disabled={disabled}
                    size="small"
                    sx={fieldSx}
                />
            )}
        </Box>
    );
});

SegmentDefinitionEditor.displayName = "SegmentDefinitionEditor";

export default SegmentDefinitionEditor;
